import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  UserGroupIcon,
  AcademicCapIcon,
  BookOpenIcon,
  TruckIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '@/hooks/useAuth'
import { getSchoolAdminDashboard, type SchoolAdminDashboardData } from '@/api/dashboardApi'
import { getAttendanceStats, getAttendance, type AttendanceStats } from '@/api/attendanceApi'
import { getGradeStats, getStreamStats, getLessonStats } from '@/api/academicApi'
import ExportModal from '@/components/ui/ExportModal'
import { type ExportConfig } from '@/utils/exportUtils'
import toast from 'react-hot-toast'

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const SchoolAnalyticsPage: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<SchoolAdminDashboardData | null>(null)
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats | null>(null)
  const [academicStats, setAcademicStats] = useState<{
    grades: { totalGrades: number; activeGrades: number; inactiveGrades: number; totalStreams: number }
    streams: { totalStreams: number; activeStreams: number; inactiveStreams: number; byGrade: Record<string, number> }
    lessons: { totalLessons: number; activeLessons: number; inactiveLessons: number; bySubject: Record<string, number>; byDifficulty: Record<string, number> }
  } | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)

  // Date range filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setMonth(date.getMonth() - 1)
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])

  useEffect(() => {
    fetchData()
  }, [startDate, endDate])

  const fetchData = async () => {
    try {
      setLoading(true)

      const [dashboardRes, attendanceRes, gradeStatsRes, streamStatsRes, lessonStatsRes] = await Promise.all([
        getSchoolAdminDashboard().catch(() => null),
        getAttendanceStats({ dateFrom: startDate, dateTo: endDate }).catch(() => null),
        getGradeStats().catch(() => null),
        getStreamStats().catch(() => null),
        getLessonStats().catch(() => null),
      ])

      if (dashboardRes?.data) {
        setDashboardData(dashboardRes.data)
      }

      if (attendanceRes?.data) {
        setAttendanceStats(attendanceRes.data)
      }

      if (gradeStatsRes || streamStatsRes || lessonStatsRes) {
        setAcademicStats({
          grades: gradeStatsRes || { totalGrades: 0, activeGrades: 0, inactiveGrades: 0, totalStreams: 0 },
          streams: streamStatsRes || { totalStreams: 0, activeStreams: 0, inactiveStreams: 0, byGrade: {} },
          lessons: lessonStatsRes || { totalLessons: 0, activeLessons: 0, inactiveLessons: 0, bySubject: {}, byDifficulty: {} },
        })
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
      toast.error('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const getExportConfig = (): ExportConfig => ({
    title: 'School Analytics Report',
    subtitle: 'Comprehensive analytics and insights for your school',
    filename: `school-analytics-${startDate}-to-${endDate}`,
    institution: {
      name: 'SmartTag School', // This would come from context/API in production
    },
    dateRange: {
      from: startDate,
      to: endDate,
    },
    generatedAt: new Date().toLocaleString(),
    summary: [
      { label: 'Total Users', value: dashboardData?.users?.total || 0 },
      { label: 'Students', value: dashboardData?.users?.students || 0 },
      { label: 'Teachers', value: dashboardData?.users?.teachers || 0 },
      { label: 'Attendance Rate', value: `${attendanceStats?.attendanceRate?.toFixed(1) || 0}%` },
      { label: 'Total Grades', value: academicStats?.grades?.totalGrades || 0 },
      { label: 'Total Streams', value: academicStats?.streams?.totalStreams || 0 },
    ],
    sections: [
      {
        title: 'Users Summary',
        columns: [
          { header: 'Category', key: 'category', width: 30 },
          { header: 'Count', key: 'count', width: 20 },
        ],
        data: [
          { category: 'Total Users', count: dashboardData?.users?.total || 0 },
          { category: 'Active Users', count: dashboardData?.users?.active || 0 },
          { category: 'Students', count: dashboardData?.users?.students || 0 },
          { category: 'Teachers', count: dashboardData?.users?.teachers || 0 },
          { category: 'Parents', count: dashboardData?.users?.parents || 0 },
          { category: 'Drivers', count: dashboardData?.users?.drivers || 0 },
        ],
      },
      {
        title: 'Attendance Summary',
        columns: [
          { header: 'Status', key: 'status', width: 30 },
          { header: 'Count', key: 'count', width: 20 },
          { header: 'Percentage', key: 'percentage', width: 20 },
        ],
        data: [
          {
            status: 'Present',
            count: attendanceStats?.presentCount || 0,
            percentage: `${((attendanceStats?.presentCount || 0) / (attendanceStats?.presentCount || 0 + attendanceStats?.absentCount || 0 + attendanceStats?.lateCount || 1) * 100).toFixed(1)}%`
          },
          {
            status: 'Absent',
            count: attendanceStats?.absentCount || 0,
            percentage: `${((attendanceStats?.absentCount || 0) / (attendanceStats?.presentCount || 0 + attendanceStats?.absentCount || 0 + attendanceStats?.lateCount || 1) * 100).toFixed(1)}%`
          },
          {
            status: 'Late',
            count: attendanceStats?.lateCount || 0,
            percentage: `${((attendanceStats?.lateCount || 0) / (attendanceStats?.presentCount || 0 + attendanceStats?.absentCount || 0 + attendanceStats?.lateCount || 1) * 100).toFixed(1)}%`
          },
        ],
      },
      {
        title: 'Academic Structure',
        columns: [
          { header: 'Category', key: 'category', width: 30 },
          { header: 'Total', key: 'total', width: 15 },
          { header: 'Active', key: 'active', width: 15 },
          { header: 'Inactive', key: 'inactive', width: 15 },
        ],
        data: [
          {
            category: 'Grades',
            total: academicStats?.grades?.totalGrades || 0,
            active: academicStats?.grades?.activeGrades || 0,
            inactive: academicStats?.grades?.inactiveGrades || 0,
          },
          {
            category: 'Streams',
            total: academicStats?.streams?.totalStreams || 0,
            active: academicStats?.streams?.activeStreams || 0,
            inactive: academicStats?.streams?.inactiveStreams || 0,
          },
          {
            category: 'Lessons',
            total: academicStats?.lessons?.totalLessons || 0,
            active: academicStats?.lessons?.activeLessons || 0,
            inactive: academicStats?.lessons?.inactiveLessons || 0,
          },
        ],
      },
      {
        title: 'Library Summary',
        columns: [
          { header: 'Metric', key: 'metric', width: 35 },
          { header: 'Value', key: 'value', width: 20 },
        ],
        data: [
          { metric: 'Total Books', value: dashboardData?.library?.totalBooks || 0 },
          { metric: 'Total Copies', value: dashboardData?.library?.totalCopies || 0 },
          { metric: 'Available Copies', value: dashboardData?.library?.availableCopies || 0 },
          { metric: 'Active Borrowings', value: dashboardData?.library?.activeBorrowings || 0 },
          { metric: 'Overdue Borrowings', value: dashboardData?.library?.overdueBorrowings || 0 },
        ],
      },
      {
        title: 'Fleet Summary',
        columns: [
          { header: 'Metric', key: 'metric', width: 35 },
          { header: 'Value', key: 'value', width: 20 },
        ],
        data: [
          { metric: 'Total Vehicles', value: dashboardData?.fleet?.totalVehicles || 0 },
          { metric: 'Active Vehicles', value: dashboardData?.fleet?.activeVehicles || 0 },
        ],
      },
    ],
  })

  // Prepare chart data
  const userDistributionData = dashboardData?.users ? [
    { name: 'Students', value: dashboardData.users.students, color: '#2563EB' },
    { name: 'Teachers', value: dashboardData.users.teachers, color: '#10B981' },
    { name: 'Parents', value: dashboardData.users.parents, color: '#F59E0B' },
    { name: 'Drivers', value: dashboardData.users.drivers, color: '#8B5CF6' },
  ].filter(item => item.value > 0) : []

  const attendanceDistributionData = attendanceStats ? [
    { name: 'Present', value: attendanceStats.presentCount, color: '#10B981' },
    { name: 'Absent', value: attendanceStats.absentCount, color: '#EF4444' },
    { name: 'Late', value: attendanceStats.lateCount, color: '#F59E0B' },
  ].filter(item => item.value > 0) : []

  const lessonsBySubjectData = academicStats?.lessons?.bySubject
    ? Object.entries(academicStats.lessons.bySubject).map(([subject, count]) => ({
        name: subject,
        count,
      }))
    : []

  const lessonsByDifficultyData = academicStats?.lessons?.byDifficulty
    ? Object.entries(academicStats.lessons.byDifficulty).map(([difficulty, count]) => ({
        name: difficulty,
        value: count,
      }))
    : []

  const weeklyLibraryData = dashboardData?.charts?.weeklyLibrary || []

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">School Analytics</h1>
          <p className="text-gray-600 mt-1">Comprehensive analytics and insights for your school</p>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
          Export Report
        </button>
      </div>

      {/* Date Range Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-600">Period:</span>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.users?.total || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Students</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.users?.students || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <BookOpenIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Library Books</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.library?.totalBooks || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Fleet Vehicles</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.fleet?.totalVehicles || 0}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Distribution</h3>
          {userDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={userDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {userDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No user data available
            </div>
          )}
        </motion.div>

        {/* Attendance Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance Overview</h3>
          {attendanceDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={attendanceDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {attendanceDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No attendance data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Library Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Library Activity</h3>
          {weeklyLibraryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={weeklyLibraryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="borrowed" stroke="#2563EB" fill="#2563EB" fillOpacity={0.3} name="Borrowed" />
                <Area type="monotone" dataKey="returned" stroke="#10B981" fill="#10B981" fillOpacity={0.3} name="Returned" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No library activity data available
            </div>
          )}
        </motion.div>

        {/* Lessons by Subject */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lessons by Subject</h3>
          {lessonsBySubjectData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={lessonsBySubjectData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No lesson data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Attendance Rate Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Attendance Rate</p>
              <p className="text-3xl font-bold mt-1">
                {attendanceStats?.attendanceRate?.toFixed(1) || 0}%
              </p>
            </div>
            <CheckCircleIcon className="h-12 w-12 text-green-200" />
          </div>
          <div className="mt-4 text-sm text-green-100">
            {(attendanceStats?.attendanceRate || 0) >= 90 ? (
              <span className="flex items-center gap-1">
                <ArrowTrendingUpIcon className="h-4 w-4" /> Excellent attendance
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <ExclamationTriangleIcon className="h-4 w-4" /> Needs improvement
              </span>
            )}
          </div>
        </motion.div>

        {/* Academic Structure Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Academic Structure</p>
              <p className="text-3xl font-bold mt-1">
                {academicStats?.grades?.totalGrades || 0} Grades
              </p>
            </div>
            <AcademicCapIcon className="h-12 w-12 text-blue-200" />
          </div>
          <div className="mt-4 text-sm text-blue-100">
            <span>{academicStats?.streams?.totalStreams || 0} Streams</span>
            <span className="mx-2">•</span>
            <span>{academicStats?.lessons?.totalLessons || 0} Lessons</span>
          </div>
        </motion.div>

        {/* Library Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-sm p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100">Library Status</p>
              <p className="text-3xl font-bold mt-1">
                {dashboardData?.library?.availableCopies || 0}
              </p>
              <p className="text-sm text-amber-100">Available Copies</p>
            </div>
            <BookOpenIcon className="h-12 w-12 text-amber-200" />
          </div>
          <div className="mt-4 text-sm text-amber-100">
            <span>{dashboardData?.library?.activeBorrowings || 0} Active Borrowings</span>
            {(dashboardData?.library?.overdueBorrowings || 0) > 0 && (
              <>
                <span className="mx-2">•</span>
                <span className="text-red-200">{dashboardData?.library?.overdueBorrowings} Overdue</span>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Lesson Difficulty Distribution */}
      {lessonsByDifficultyData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lessons by Difficulty Level</h3>
          <div className="grid grid-cols-3 gap-4">
            {lessonsByDifficultyData.map((item, index) => (
              <div
                key={item.name}
                className="text-center p-4 bg-gray-50 rounded-lg"
              >
                <p className="text-sm text-gray-600">{item.name}</p>
                <p className="text-2xl font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        config={getExportConfig()}
        title="Export School Analytics"
      />
    </div>
  )
}

export default SchoolAnalyticsPage
